import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ShieldCheck, Layers, Heart } from 'lucide-react';
import { useLang, ui } from '../i18n/LanguageContext';
import { aboutContent } from '../mock/data';
import useReveal from '../hooks/useReveal';

const valueIcons = [ShieldCheck, Layers, Heart];

const PageHero = ({ tag, title, image }) => (
  <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 bg-[#161514] overflow-hidden">
    <div className="absolute inset-0 opacity-30">
      <img src={image} alt="" className="w-full h-full object-cover grayscale" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#161514] via-[#161514]/60 to-[#161514]/40" />
    </div>
    <div className="relative max-w-[1280px] mx-auto px-5 md:px-8">
      <p className="eyebrow text-champagne mb-5 fade-in-up">{tag}</p>
      <h1 className="font-serif text-white text-4xl md:text-6xl leading-[1.08] max-w-3xl fade-in-up">{title}</h1>
    </div>
  </section>
);

export { PageHero };

const About = () => {
  const { t } = useLang();
  useReveal();
  const values = t(aboutContent.values);

  return (
    <div>
      <PageHero
        tag={t(aboutContent.tag)}
        title={t(aboutContent.title)}
        image="https://customer-assets-rejwkqb3.emergentagent.net/job_transition-familiale/artifacts/jq19y0oe_20240106_CEF_220.webp"
      />

      <section className="py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7 reveal">
            <div className="space-y-6 text-lg text-[#3a3833] leading-relaxed">
              {t(aboutContent.paragraphs).map((p, i) => (
                <p key={i} className={i === 1 ? 'font-serif italic text-2xl text-[#161514] border-l-2 border-champagne pl-6 py-2' : ''}>{p}</p>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 reveal">
            <div className="img-zoom aspect-[3/4]">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_transition-familiale/artifacts/2dxbauyw_20240106_CEF_01.webp"
                alt="Professionnelle du Centre d'expertise familiale"
                className="w-full h-full object-cover grayscale"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pb-4 md:pb-8" data-testid="about-team-photo">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 reveal">
          <div className="img-zoom aspect-[16/9] md:aspect-[21/9] bg-secondary">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_transition-familiale/artifacts/qbvpel7e_20240106_CEF_226.webp"
              alt="L'équipe du Centre d'expertise familiale"
              className="w-full h-full object-cover object-top grayscale"
            />
          </div>
          <p className="mt-5 text-sm text-[#6b6862] italic text-center">
            {t({ fr: 'L’équipe multidisciplinaire du Centre d’expertise familiale', en: 'The multidisciplinary team of the Centre d’expertise familiale' })}
          </p>
        </div>
      </section>


      <section className="bg-secondary py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {values.map((v, i) => {
              const Ic = valueIcons[i];
              return (
                <div key={i} className="reveal" style={{ transitionDelay: `${i * 120}ms` }}>
                  <Ic size={32} strokeWidth={1.3} className="text-champagne mb-6" />
                  <h3 className="font-serif text-2xl mb-3">{v.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center reveal">
          <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-8">{t(ui.team.title)}</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/equipe" className="inline-flex items-center justify-center gap-2 bg-[#161514] text-white px-8 py-4 text-sm hover:bg-champagne transition-all duration-300">
              {t(ui.cta.meetTeam)} <ArrowUpRight size={17} />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 border border-[#161514] px-8 py-4 text-sm hover:bg-[#161514] hover:text-white transition-all duration-300">
              {t(ui.cta.contact)}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
